// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TvInteriorIdCache } from '../Caches/TvInteriorIdCache';
import { DataCache } from '../../DataCache';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { TvSymbolConvertor } from '../Convertors/TvSymbolConvertor';
import { TvQuoteUtils } from '../../UtilsClasses/TvQuoteUtils';
import { TvAccountHelper } from '../Helpers/TvAccountHelper';
import throttle from 'lodash.throttle';
export class TvQuoteSubscriptionItem {
    constructor() {
        this.subscribedSymbols = [];
        this.throttledSendRealtimeData = throttle(() => {
            this.sendRealtimeData();
        }, TvQuoteSubscriptionItem.THROTTLE_DELAY, { leading: true, trailing: true });
    }
    static getQuoteDataArray(symbols, account) {
        const data = [];
        for (const symbol of symbols) {
            const interiorId = TvInteriorIdCache.getInteriorId(symbol);
            const instrument = DataCache.getInstrumentByName(interiorId);
            if (!instrument) {
                continue;
            }
            const quoteData = this.getQuoteData(instrument, account, symbol);
            data.push(quoteData);
        }
        return data;
    }
    static getQuoteData(instrument, account, symbol) {
        const askPrice = instrument.Level1.GetAsk(account);
        const bidPrice = instrument.Level1.GetBid(account);
        const spreadPrice = askPrice - bidPrice;
        const change = instrument.Level1.GetChange(account);
        const changePercent = instrument.Level1.GetChangePercent(account);
        const highPrice = instrument.InstrumentDayInfo.getHigh(account);
        const lowPrice = instrument.InstrumentDayInfo.getLow(account);
        const lastPrice = instrument.InstrumentDayInfo.getClose(account);
        const openPrice = instrument.InstrumentDayInfo.getOpen(account);
        const prevClosePrice = instrument.InstrumentDayInfo.getPrevClose(account);
        const volume = instrument.InstrumentDayInfo.TotalVolume();
        const symbolName = TvSymbolConvertor.getSymbolName(instrument);
        const quoteValues = {
            ask: TvQuoteUtils.replaceNaN(askPrice),
            bid: TvQuoteUtils.replaceNaN(bidPrice),
            ch: TvQuoteUtils.replaceNaN(change),
            chp: TvQuoteUtils.replaceNaN(changePercent),
            description: instrument.DescriptionValue(),
            exchange: instrument.TradingExchange, // ?
            high_price: TvQuoteUtils.replaceNaN(highPrice),
            low_price: TvQuoteUtils.replaceNaN(lowPrice),
            lp: TvQuoteUtils.replaceNaN(lastPrice),
            open_price: TvQuoteUtils.replaceNaN(openPrice),
            original_name: symbolName,
            prev_close_price: TvQuoteUtils.replaceNaN(prevClosePrice),
            short_name: symbolName,
            spread: TvQuoteUtils.replaceNaN(spreadPrice),
            volume: TvQuoteUtils.replaceNaN(volume)
        };
        return {
            n: symbol,
            s: 'ok',
            v: quoteValues
        };
    }
    subscribe(symbols, fastSymbols, onRealtimeCallback) {
        this.realtimeCallback = onRealtimeCallback;
        const prevSubscribedSymbols = this.subscribedSymbols;
        this.subscribedSymbols = symbols;
        const removedSymbols = prevSubscribedSymbols.filter((element) => !this.subscribedSymbols.includes(element));
        this.unsubscribeSymbols(removedSymbols);
        const addedSymbols = this.subscribedSymbols.filter((element) => !prevSubscribedSymbols.includes(element));
        for (const symbol of addedSymbols) {
            const interiorId = TvInteriorIdCache.getInteriorId(symbol);
            const instrument = DataCache.getInstrumentByName(interiorId);
            if (!instrument) {
                continue;
            }
            DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_LEVEL1);
            DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_TRADES);
        }
    }
    unsubscribe() {
        this.unsubscribeSymbols(this.subscribedSymbols);
    }
    unsubscribeSymbols(symbols) {
        for (const symbol of symbols) {
            const interiorId = TvInteriorIdCache.getInteriorId(symbol);
            const instrument = DataCache.getInstrumentByName(interiorId);
            if (!instrument) {
                continue;
            }
            DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_LEVEL1);
            DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_TRADES);
        }
    }
    newQuote(msg) {
        this.throttledSendRealtimeData();
    }
    sendRealtimeData() {
        if (!this.realtimeCallback)
            return;
        const account = TvAccountHelper.getCurrentAccount();
        const data = TvQuoteSubscriptionItem.getQuoteDataArray(this.subscribedSymbols, account);
        this.realtimeCallback(data);
    }
}
TvQuoteSubscriptionItem.THROTTLE_DELAY = 200;
